import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Collapse,
  Tag,
  Spin,
  Empty,
  Pagination,
  message,
  Result,
  Modal,
  Checkbox
} from 'antd'
import { DatePicker as MobileDatePicker } from 'antd-mobile'
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  CalendarOutlined,
  UserOutlined,
  DollarOutlined,
  ShoppingOutlined,
  EditOutlined,
  DownloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  IdcardOutlined,
  CameraOutlined
} from '@ant-design/icons'
import styled, { createGlobalStyle } from 'styled-components'
import dayjs from 'dayjs'

import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../utils/api'
import ServicePhotoThumbs from '../components/ServicePhotoThumbs'
import CustomerNameLink from '../components/common/CustomerNameLink'
import { listServicePhotosByBookings } from '../api/servicePhoto'
import { formatTz } from '../utils/timezone'
import { now as tzNow } from '../utils/timezone'

const { RangePicker } = DatePicker

const CheckoutRecordsGlobalStyle = createGlobalStyle`

 .ant-layout-content {
    padding: 0 !important;
  }
 @media (max-width: 768px) {
   .ant-layout-content {
    margin: 30px 0 0 0 !important;
    padding: 0 !important;
  }
 }
`

const PageWrapper = styled.div`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
`

const PageContainer = styled.div`
  padding: 20px 24px;
  min-height: calc(100vh - 120px);
  background: transparent;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const SearchCard = styled.div`
  /* 完全去掉外框、底色、padding，跟頁面融為一體 */
  background: transparent;
  border-radius: 0;
  padding: 0;
  margin-bottom: 16px;
  border: none;
  box-shadow: none;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: 8px;
  }

  /* 縮小 Form.Item 預設的 margin-bottom，讓欄位不要灌太多空白 */
  .ant-form-item {
    margin-bottom: 0;
  }

  /* 平板（兩欄）時佔滿整列：關鍵字、訂單編號剛好一列，
     日期區間若只佔半格，右邊會整格空著；手機版的開始／結束兩顆按鈕也才能並排 */
  @media (max-width: 1024px) {
    .filter-span-full {
      grid-column: 1 / -1;
    }
  }
`

// 手機版日期區間的兩顆按鈕：平板並排、手機上下
const MobileDateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }

  /* icon、日期文字靠左，「清除」靠右 */
  .date-btn {
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    text-align: left;
  }
  .date-btn .date-text {
    flex: 1;
    min-width: 0;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .date-btn .date-text.placeholder {
    color: #bfbfbf;
  }
`

const FilterActions = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    margin-top: 12px;
    justify-content: stretch;

    button {
      flex: 1;
    }
  }
`

/* CSV 匯出按鈕：手機 / 平板（≤768px）隱藏，避免功能擠 */
const CsvExportButton = styled(Button)`
  @media (max-width: 768px) {
    display: none;
  }
`

const SortBar = styled.div`
  margin-top: 12px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  /* 桌機 + 手機都不再用框，只用一條淡淡的分隔線輔助 */
  padding: 8px 0;
  background: transparent;
  border: none;

  @media (max-width: 768px) {
    margin-top: 4px;
    margin-bottom: 8px;
    gap: 10px 20px;
  }
`

const SortBarLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #0f2562;
  white-space: nowrap;
`

const SortBarSelect = styled.div`
  min-width: 220px;
  flex: 1;
  max-width: 300px;

  /* 平板：標籤與下拉同一列，下拉吃掉剩下的寬度 */
  @media (max-width: 1024px) {
    flex: 1 1 0;
    max-width: none;
    min-width: 0;
  }

  /* 手機：下拉獨佔一列 */
  @media (max-width: 600px) {
    flex: 1 1 100%;
  }
`

/* 兩個勾選放進同一個容器：平板／手機時一起換到下一列並排，
   不再各自佔滿一整列、上下間距忽大忽小 */
const SortBarChecks = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin-left: auto;

  @media (max-width: 1024px) {
    margin-left: 0;
    flex: 1 1 100%;
  }
`

const SortBarCheckbox = styled(Form.Item)`
  margin-bottom: 0 !important;

  .ant-form-item-control-input {
    min-height: 0;
  }
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`

const RecordCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* 拿掉厚重的陰影，改用一道淡邊框，視覺更乾淨 */
  border: 1px solid #f0f0f0;
  box-shadow: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    border-color: #d9e1f0;
    box-shadow: 0 2px 8px rgba(15, 37, 98, 0.04);
  }

  @media (max-width: 600px) {
    padding: 14px 16px;
    gap: 10px;
    border-radius: 10px;
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #0f2562;
    word-break: break-all;
  }
`

const HeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;

  span {
    font-size: 12px;
    color: #8c8c8c;
  }
`

const Section = styled.div`
  display: grid;
  gap: 6px;
  /* 改用內部分隔線取代每個 section 的獨立框，不再有「框中框」 */
  padding-top: 10px;
  border-top: 1px dashed #f0f0f0;

  &:first-of-type {
    padding-top: 0;
    border-top: none;
  }
`

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0f2562;
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.85;
`

const SectionContent = styled.div`
  font-size: 13.5px;
  color: #576079;
  display: grid;
  gap: 4px;

  strong {
    color: #0f2562;
    font-weight: 500;
  }
`

const ServicesList = styled.ul`
  display: grid;
  gap: 8px;
  padding-left: 16px;
  margin: 0;
  color: #576079;

  li {
    list-style: disc;
  }
`

const PaymentList = styled.div`
  display: grid;
  gap: 6px;
`

const PaymentItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;

  /* 只有「金額」那一欄要加粗；用 > 限定直接子層，
     否則標籤裡的附註小字（定金的收款日期）也會被染成深藍粗體 */
  > span:last-child {
    font-weight: 600;
    color: #0f2562;
  }
`

/** 付款方式標籤後面的附註小字（目前用在定金的收款日期） */
const PaymentNote = styled.span`
  margin-left: 6px;
  font-size: 12px;
  font-weight: 400;
  color: #8c8c8c;
`

const StatusTag = styled(Tag)`
  margin: 0;
  padding: 2px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
`

const ResultWrapper = styled.div`
  background: transparent;
  min-height: 240px;
`

const PaginationWrapper = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 600px) {
    justify-content: center;
  }
`

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px dashed #f0f0f0;

  button {
    flex: 1 1 calc(50% - 4px);
    min-width: 120px;
  }

  @media (max-width: 600px) {
    button {
      flex: 1 1 100%;
    }
  }
`

const CollapseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`

/**
 * 訂單來源。
 *
 * 結帳紀錄現在不只有櫃檯打的單：線上商店訂單付款後會補一筆、
 * 堂票手動發放也會補一筆（視同結帳）。店家對帳時常常只想看其中一種。
 */
const sourceOptions = [
  { value: 'checkout', label: '櫃檯結帳' },
  { value: 'shop_order', label: '線上商店訂單' },
  { value: 'class_ticket', label: '堂票發放' },
  { value: 'class_ticket_redeem', label: '堂票核銷' },
  { value: 'booking_deposit', label: '定金沒收' }
]

const SOURCE_LABEL = {
  checkout: '櫃檯結帳',
  shop_order: '線上訂單',
  class_ticket: '堂票發放',
  class_ticket_redeem: '堂票核銷',
  booking_deposit: '定金沒收'
}

const statusColors = {
  paid: 'green',
  paid_partially: 'orange',
  draft: 'default',
  cancelled: 'red',
  voided: 'red',
  confirmed: 'blue'
}

/**
 * 定金的收款方式（結帳單上的快照）。
 *
 * 定金是在結帳之前就收掉的錢，單子上只寫「定金 $500」看不出是刷卡還是現金 ——
 * 對帳時分不出哪些會被收單行扣手續費、哪些是現金進抽屜，所以要標出來。
 */
const DEPOSIT_METHOD_LABELS = {
  credit: '刷卡',
  cash: '現金',
  bank_transfer: '匯款'
}

const paymentMethodLabels = [
  { value: 'cash', label: '現金' },
  { value: 'storedValue', label: '儲值金' },
  { value: 'transfer', label: '匯款' },
  { value: 'deposit', label: '定金' },
  { value: 'cardPayment', label: '刷卡' },
  { value: 'mobilePayment', label: '行動支付' },
  { value: 'voucher', label: '優惠券' }
]

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'NT$ 0'
  const numeric = Number(value)
  const safeNum = Number.isFinite(numeric) ? numeric : 0
  return `NT$ ${safeNum.toLocaleString()}`
}

const CHECKOUT_STATUS_CSV_LABEL = {
  draft: '草稿',
  confirmed: '已確認',
  paid_partially: '部分付款',
  paid: '已付款',
  voided: '已作廢'
}

const escapeCheckoutCsvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v)
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

const checkoutPaymentSummaryForCsv = (payment = {}) => {
  const parts = paymentMethodLabels
    .map(({ value, label }) => {
      const n = Number(payment[value]) || 0
      return n > 0 ? `${label}:${n}` : null
    })
    .filter(Boolean)
  // 定金的收款方式：跟在「定金:500」後面，讓匯出的報表也分得出刷卡定金
  const depositLabel = DEPOSIT_METHOD_LABELS[payment.depositMethod]
  if (depositLabel && Number(payment.deposit) > 0) {
    const idx = parts.findIndex((t) => t.startsWith('定金:'))
    if (idx >= 0) parts[idx] = `${parts[idx]}(${depositLabel})`
  }
  // 金流手續費（店家支出）單獨附註，不併入付款方式
  const fee = Number(payment.paymentFee) || 0
  if (fee > 0) parts.push(`金流手續費(店家支出):${fee}`)
  return parts.join('; ')
}

const orderPassesCheckoutExportFilters = (order, filters) => {
  const rec = Number(order.amounts?.receivable)
  if (
    filters.minAmount !== undefined &&
    filters.minAmount !== null &&
    filters.minAmount !== '' &&
    Number.isFinite(Number(filters.minAmount)) &&
    rec < Number(filters.minAmount)
  ) {
    return false
  }
  if (
    filters.maxAmount !== undefined &&
    filters.maxAmount !== null &&
    filters.maxAmount !== '' &&
    Number.isFinite(Number(filters.maxAmount)) &&
    rec > Number(filters.maxAmount)
  ) {
    return false
  }
  if (filters.paymentMethod) {
    const v = Number(order.payment?.[filters.paymentMethod]) || 0
    if (v <= 0) return false
  }
  return true
}

const serviceLineUnit = (s) => {
  if (s.revenueAmount !== undefined && s.revenueAmount !== null) {
    const n = Number(s.revenueAmount)
    if (Number.isFinite(n)) return n
  }
  return Number(s.price) || 0
}

const productLineUnit = (p) => {
  if (p.revenueAmount !== undefined && p.revenueAmount !== null) {
    const n = Number(p.revenueAmount)
    if (Number.isFinite(n)) return n
  }
  return Number(p.price) || 0
}

const checkoutOrdersToCsvRows = (orders) => {
  const header = [
    '消費日期',
    '開始時間',
    '客戶姓名',
    '客戶電話',
    '類別',
    '項目名稱',
    '數量',
    '單價',
    '明細金額',
    '技術人員或銷售',
    // 向客人加收的服務費：已含在「訂單應收」裡，單獨一欄才對得出
    // 應收為什麼比明細金額總和多一筆。沒開加收服務費的店家整欄都是 0。
    '加收服務費',
    '訂單應收',
    '訂單已付',
    '訂單未付',
    '付款摘要',
    '訂單狀態',
    '備註'
  ]
  const rows = [header.map(escapeCheckoutCsvCell)]

  for (const order of orders) {
    const date = order.time?.date || ''
    const startTime = order.time?.startTime || ''
    const cust = order.customer || {}
    const name = cust.name || ''
    const phone = cust.phone || ''
    const amounts = order.amounts || {}
    const receivable = Number(amounts.receivable) || 0
    const surcharge = Number(amounts.surchargeAmount) || 0
    const paid = Number(amounts.paid) || 0
    const unpaid = Number(amounts.unpaid) || 0
    const payment = order.payment || {}
    const payText = checkoutPaymentSummaryForCsv(payment)
    const status =
      CHECKOUT_STATUS_CSV_LABEL[order.status] || order.status || ''
    const remarks = (order.remarks || '').replace(/\r?\n/g, ' ')

    const pushRow = (cols) => {
      rows.push(cols.map(escapeCheckoutCsvCell))
    }

    const base = () => ({
      date,
      startTime,
      name,
      phone,
      surcharge,
      receivable,
      paid,
      unpaid,
      payText,
      status,
      remarks
    })

    const services = order.services || order.items || []
    const products = order.products || []
    const coursePurchases = order.courses?.purchases || []
    const courseUsages = order.courses?.usages || []

    let anyLine = false
    const b = base()

    for (const s of services) {
      anyLine = true
      const qty = Math.max(1, Number(s.quantity) || 1)
      const unit = serviceLineUnit(s)
      const line = unit * qty
      const staff =
        s.designerName ||
        s.personnelName ||
        s.assistantName ||
        (typeof s.personnelId === 'object' && s.personnelId !== null
          ? s.personnelId.name
          : '') ||
        ''
      const itemName = s.serviceName || s.name || '服務'
      pushRow([
        b.date,
        b.startTime,
        b.name,
        b.phone,
        '服務',
        itemName,
        qty,
        unit,
        line,
        staff,
        b.surcharge,
        b.receivable,
        b.paid,
        b.unpaid,
        b.payText,
        b.status,
        b.remarks
      ])
    }

    for (const p of products) {
      anyLine = true
      const qty = Math.max(1, Number(p.quantity) || 1)
      const unit = productLineUnit(p)
      const line = unit * qty
      const staff = p.productSalesPersonnelName || ''
      const itemName = p.productName || '商品'
      pushRow([
        b.date,
        b.startTime,
        b.name,
        b.phone,
        '商品',
        itemName,
        qty,
        unit,
        line,
        staff,
        b.surcharge,
        b.receivable,
        b.paid,
        b.unpaid,
        b.payText,
        b.status,
        b.remarks
      ])
    }

    for (const c of coursePurchases) {
      anyLine = true
      const qty = Math.max(1, Number(c.quantity) || 1)
      const unit = Number(c.price) || 0
      const line = unit * qty
      const staff = c.salesPersonnelName || ''
      pushRow([
        b.date,
        b.startTime,
        b.name,
        b.phone,
        '課程購買',
        c.courseName || '課程',
        qty,
        unit,
        line,
        staff,
        b.surcharge,
        b.receivable,
        b.paid,
        b.unpaid,
        b.payText,
        b.status,
        b.remarks
      ])
    }

    for (const c of courseUsages) {
      anyLine = true
      const qty = Math.max(1, Number(c.usedUnits) || 1)
      pushRow([
        b.date,
        b.startTime,
        b.name,
        b.phone,
        '課程使用',
        c.courseName || '課程',
        qty,
        '',
        '',
        '',
        b.surcharge,
        b.receivable,
        b.paid,
        b.unpaid,
        b.payText,
        b.status,
        b.remarks
      ])
    }

    if (!anyLine) {
      pushRow([
        b.date,
        b.startTime,
        b.name,
        b.phone,
        '訂單',
        '(無明細項目)',
        1,
        0,
        0,
        '',
        b.surcharge,
        b.receivable,
        b.paid,
        b.unpaid,
        b.payText,
        b.status,
        b.remarks
      ])
    }
  }

  return rows
}

const CheckoutRecordsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { hasFeature, permissionsLoaded } = useMembership()
  const { canAny } = usePermission()
  const [form] = Form.useForm()
  const [records, setRecords] = useState([])
  // { [bookingId]: { photos: [...], total: n } }，由批次 API 一次撈回
  const [servicePhotoMap, setServicePhotoMap] = useState({})
  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })
  const [isMobile, setIsMobile] = useState(false)
  const [showMobileStartPicker, setShowMobileStartPicker] = useState(false)
  const [showMobileEndPicker, setShowMobileEndPicker] = useState(false)
  const [isUpdateDateModalVisible, setIsUpdateDateModalVisible] =
    useState(false) // 改日期 Modal 顯示狀態
  const [selectedRecordForDateUpdate, setSelectedRecordForDateUpdate] =
    useState(null) // 選中要改日期的記錄
  const [updateDateForm] = Form.useForm() // 改日期表單
  const [updateDatePickerVisible, setUpdateDatePickerVisible] = useState(false) // 改日期日期選擇器顯示狀態
  const [isUpdateRemarksModalVisible, setIsUpdateRemarksModalVisible] =
    useState(false) // 改備註 Modal 顯示狀態
  const [selectedRecordForRemarksUpdate, setSelectedRecordForRemarksUpdate] =
    useState(null) // 選中要改備註的記錄
  const [updateRemarksForm] = Form.useForm() // 改備註表單
  // 排序狀態：默認按消費日期降序（最新在前）
  const [sortConfig, setSortConfig] = useState({
    sortField: 'time.date',
    sortOrder: 'desc'
  })
  const [exportCsvLoading, setExportCsvLoading] = useState(false)

  const filtersRef = useRef({})

  const skipUrlSyncRef = useRef(false)
  // 將查詢參數寫入 URL（搜尋、分頁、排序變更時）
  const syncParamsToUrl = useCallback(
    (updates) => {
      skipUrlSyncRef.current = true
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          Object.keys(updates).forEach((k) => {
            const v = updates[k]
            if (v === undefined || v === null || v === '') next.delete(k)
            else next.set(k, String(v))
          })
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  // 將方案權限判斷固定為布林值，避免每次 render 重新生成函式導致 useEffect 重跑
  const hasCheckoutAccess = useMemo(
    () => hasFeature('checkout'),
    [hasFeature, user?.businessId]
  )

  // 所有 Hooks 必須在權限檢查之前調用
  const fetchEmployees = useCallback(async () => {
    if (!user?.businessId || !hasCheckoutAccess) return
    try {
      const response = await api.get(`/api/employees/${user.businessId}`)
      setEmployees(response.data || [])
    } catch (error) {
      console.error('取得員工清單失敗', error)
    }
  }, [user?.businessId, hasCheckoutAccess])

  const fetchRecords = useCallback(
    async (page = 1, pageSize = 10, filters = {}) => {
      if (!user?.businessId || !hasCheckoutAccess) return
      setLoading(true)

      try {
        const params = {
          page,
          pageSize
        }

        if (filters.keyword) params.keyword = filters.keyword.trim()
        if (filters.orderId) params.orderId = filters.orderId.trim()
        if (filters.personnelId) params.personnelId = filters.personnelId
        // 如果用户明确筛选了状态，使用用户选择的状态
        // 如果没有筛选状态，默认排除已作废的订单（在 API 层面处理，确保 total 准确）
        if (filters.status) {
          params.status = filters.status
        } else {
          // 默认排除 voided 订单，可以通过传递多个状态值来实现
          // 如果后端支持，可以传递 status: '!voided' 或类似的语法
          // 或者传递所有非 voided 的状态
          // 这里先尝试传递排除 voided 的逻辑，如果后端不支持，再在前端过滤
          // 假设后端支持排除特定状态，可以这样：params.excludeStatus = 'voided'
          // 或者如果后端支持多个状态值：params.status = ['completed', 'pending', 'cancelled', ...]
          // 为了兼容性，我们先在前端过滤，但确保 total 使用后端返回的值
        }
        if (filters.paymentMethod) params.paymentMethod = filters.paymentMethod
        // 訂單來源：櫃檯結帳 / 線上商店訂單 / 堂票發放
        if (filters.source) params.source = filters.source
        // 只看有施作照片（照片掛在預約上，後端用 bookingId 比對）
        if (filters.hasPhoto) params.hasPhoto = 1
        if (filters.dateRange?.length === 2) {
          params.dateStart = filters.dateRange[0].format('YYYY-MM-DD')
          params.dateEnd = filters.dateRange[1].format('YYYY-MM-DD')
        }
        if (
          filters.minAmount !== undefined &&
          filters.minAmount !== null &&
          filters.minAmount !== ''
        ) {
          params.amountMin = Number(filters.minAmount)
        }
        if (
          filters.maxAmount !== undefined &&
          filters.maxAmount !== null &&
          filters.maxAmount !== ''
        ) {
          params.amountMax = Number(filters.maxAmount)
        }

        // 添加排序參數（默認按消費日期降序，最新在前）
        // 如果不傳遞 sortField 和 sortOrder，後端默認按消費日期降序
        if (filters.sortField) {
          params.sortField = filters.sortField
        } else {
          params.sortField = sortConfig.sortField
        }
        if (filters.sortOrder) {
          params.sortOrder = filters.sortOrder
        } else {
          params.sortOrder = sortConfig.sortOrder
        }

        const response = await api.get(
          `/api/businesses/${user.businessId}/checkout/orders`,
          { params }
        )

        const responseData = response.data || {}
        const ordersPayload =
          responseData.data?.orders ||
          responseData.orders ||
          responseData.data ||
          responseData.records ||
          responseData.items ||
          responseData
        const recordsSource = Array.isArray(ordersPayload)
          ? ordersPayload
          : ordersPayload || []
        let recordsData = Array.isArray(recordsSource)
          ? recordsSource
          : Array.isArray(recordsSource?.data)
            ? recordsSource.data
            : Array.isArray(recordsSource?.records)
              ? recordsSource.records
              : Array.isArray(recordsSource?.items)
                ? recordsSource.items
                : Array.isArray(recordsSource?.list)
                  ? recordsSource.list
                  : []

        // 取得後端分頁資訊（盡量兼容多種結構）
        const paginationMeta =
          responseData.pagination ||
          responseData.data?.pagination ||
          ordersPayload?.pagination ||
          ordersPayload?.meta?.pagination ||
          recordsSource?.pagination

        // 优先使用后端返回的总数
        let total =
          paginationMeta?.total ??
          responseData.total ??
          responseData.totalCount ??
          ordersPayload?.total ??
          ordersPayload?.totalCount ??
          ordersPayload?.meta?.total ??
          recordsData.length

        // 過濾 voided 訂單的規則（優先順序）：
        //   1. 用戶明確選了 status 篩選 → 用該 status，不額外過濾
        //   2. 用戶勾「顯示已作廢訂單」→ 不過濾，全部顯示
        //   3. 用戶按訂單編號或關鍵字精準搜尋 → 不過濾，避免「搜不到誤以為不存在」
        //   4. 預設（一般瀏覽）→ 過濾掉 voided
        const isExactSearch = !!(
          (filters.orderId && String(filters.orderId).trim()) ||
          (filters.keyword && String(filters.keyword).trim())
        )
        const shouldHideVoided =
          !filters.status && !filters.includeVoided && !isExactSearch
        if (shouldHideVoided) {
          recordsData = recordsData.filter(
            (record) => record.status !== 'voided'
          )
          // 注意：這裡只過濾當前頁，total 仍用後端值
        }

        // 優先使用 API 返回的分頁資訊，確保與後端一致
        const currentPage = paginationMeta?.page ?? page
        const currentPageSize = paginationMeta?.pageSize ?? pageSize

        setRecords(recordsData)
        setPagination((prev) => ({
          ...prev,
          current: currentPage,
          pageSize: currentPageSize,
          total: total || recordsData.length
        }))
      } catch (error) {
        console.error('獲取結帳紀錄失敗', error)
        message.error(
          error.response?.data?.message ||
            error.message ||
            '無法獲取結帳紀錄，請稍後再試'
        )
      } finally {
        setLoading(false)
      }
    },
    [user?.businessId, hasCheckoutAccess]
  )

  const handleSearch = useCallback(async () => {
    const values = form.getFieldsValue()
    const nextFilters = {
      keyword: values.keyword || undefined,
      orderId: values.orderId || undefined,
      personnelId: values.personnelId || undefined,
      status: values.status || undefined,
      paymentMethod: values.paymentMethod || undefined,
      dateRange: values.dateRange || undefined,
      // 訂單來源（原本漏傳，選了來源按搜尋不會生效）
      source: values.source || undefined,
      // 「顯示已作廢訂單」勾選 — fetchRecords 內會用這個判斷是否要過濾 voided
      includeVoided: !!values.includeVoided,
      // 「只看有施作照片」勾選
      hasPhoto: !!values.hasPhoto,
      minAmount:
        values.minAmount !== undefined && values.minAmount !== ''
          ? values.minAmount
          : undefined,
      maxAmount:
        values.maxAmount !== undefined && values.maxAmount !== ''
          ? values.maxAmount
          : undefined
    }
    filtersRef.current = nextFilters
    setPagination((prev) => ({ ...prev, current: 1 }))
    await fetchRecords(1, pagination.pageSize, nextFilters)
    syncParamsToUrl({
      page: 1,
      pageSize: pagination.pageSize,
      keyword: nextFilters.keyword,
      orderId: nextFilters.orderId,
      dateStart: nextFilters.dateRange?.[0]?.format('YYYY-MM-DD'),
      dateEnd: nextFilters.dateRange?.[1]?.format('YYYY-MM-DD'),
      personnelId: nextFilters.personnelId,
      status: nextFilters.status,
      paymentMethod: nextFilters.paymentMethod,
      hasPhoto: nextFilters.hasPhoto ? 1 : undefined,
      amountMin: nextFilters.minAmount,
      amountMax: nextFilters.maxAmount,
      sort: `${sortConfig.sortField}-${sortConfig.sortOrder}`
    })
  }, [fetchRecords, form, pagination.pageSize, syncParamsToUrl, sortConfig])

  const handleReset = useCallback(async () => {
    form.resetFields()
    // 重置排序為默認值（按消費日期降序，最新在前）
    setSortConfig({
      sortField: 'time.date',
      sortOrder: 'desc'
    })
    filtersRef.current = {}
    setPagination((prev) => ({ ...prev, current: 1 }))
    await fetchRecords(1, pagination.pageSize, {
      sortField: 'time.date',
      sortOrder: 'desc'
    })
    syncParamsToUrl({})
  }, [fetchRecords, form, pagination.pageSize, syncParamsToUrl])

  /** 依目前篩選條件分頁拉齊資料後匯出 CSV（UTF-8 BOM，不含訂單編號） */
  const handleExportCheckoutCsv = useCallback(async () => {
    if (!user?.businessId || !hasCheckoutAccess) return
    const filters = {
      ...filtersRef.current,
      sortField: sortConfig.sortField,
      sortOrder: sortConfig.sortOrder
    }
    const EXPORT_PAGE_SIZE = 100
    const MAX_PAGES = 500

    const buildParams = (pageNum) => {
      const params = { page: pageNum, pageSize: EXPORT_PAGE_SIZE }
      if (filters.keyword) params.keyword = filters.keyword.trim()
      if (filters.orderId) params.orderId = filters.orderId.trim()
      if (filters.personnelId) params.personnelId = filters.personnelId
      if (filters.status) params.status = filters.status
      if (filters.paymentMethod) params.paymentMethod = filters.paymentMethod
      if (filters.source) params.source = filters.source
      if (filters.hasPhoto) params.hasPhoto = 1
      if (filters.dateRange?.length === 2) {
        params.dateStart = filters.dateRange[0].format('YYYY-MM-DD')
        params.dateEnd = filters.dateRange[1].format('YYYY-MM-DD')
      }
      if (
        filters.minAmount !== undefined &&
        filters.minAmount !== null &&
        filters.minAmount !== ''
      ) {
        params.amountMin = Number(filters.minAmount)
      }
      if (
        filters.maxAmount !== undefined &&
        filters.maxAmount !== null &&
        filters.maxAmount !== ''
      ) {
        params.amountMax = Number(filters.maxAmount)
      }
      params.sortField = filters.sortField || 'time.date'
      params.sortOrder = filters.sortOrder || 'desc'
      return params
    }

    const normalizeOrdersResponse = (response) => {
      const responseData = response.data || {}
      const ordersPayload =
        responseData.data?.orders ||
        responseData.orders ||
        responseData.data ||
        responseData.records ||
        responseData.items ||
        responseData
      const recordsSource = Array.isArray(ordersPayload)
        ? ordersPayload
        : ordersPayload || []
      const recordsData = Array.isArray(recordsSource)
        ? recordsSource
        : Array.isArray(recordsSource?.data)
          ? recordsSource.data
          : Array.isArray(recordsSource?.records)
            ? recordsSource.records
            : Array.isArray(recordsSource?.items)
              ? recordsSource.items
              : Array.isArray(recordsSource?.list)
                ? recordsSource.list
                : []
      const paginationMeta =
        responseData.pagination ||
        responseData.data?.pagination ||
        ordersPayload?.pagination ||
        ordersPayload?.meta?.pagination ||
        recordsSource?.pagination
      return { recordsData, paginationMeta }
    }

    const resolveTotalPages = (meta, pageSizeNum) => {
      if (meta?.totalPages != null && Number(meta.totalPages) >= 1) {
        return Number(meta.totalPages)
      }
      const t = meta?.total ?? meta?.totalCount
      if (t != null && Number(t) >= 0) {
        return Math.max(1, Math.ceil(Number(t) / pageSizeNum))
      }
      return 1
    }

    setExportCsvLoading(true)
    try {
      let pageNum = 1
      let totalPages = 1
      const allOrders = []

      while (pageNum <= totalPages && pageNum <= MAX_PAGES) {
        const response = await api.get(
          `/api/businesses/${user.businessId}/checkout/orders`,
          { params: buildParams(pageNum) }
        )
        const { recordsData, paginationMeta } =
          normalizeOrdersResponse(response)
        let batch = recordsData
        // 與列表頁一致：includeVoided 勾選 / 精準搜尋 / 明確選 status 時不過濾
        const isExactSearchExport = !!(
          (filters.orderId && String(filters.orderId).trim()) ||
          (filters.keyword && String(filters.keyword).trim())
        )
        const shouldHideVoidedExport =
          !filters.status && !filters.includeVoided && !isExactSearchExport
        if (shouldHideVoidedExport) {
          batch = batch.filter((r) => r.status !== 'voided')
        }
        batch = batch.filter((o) =>
          orderPassesCheckoutExportFilters(o, filters)
        )
        allOrders.push(...batch)
        totalPages = resolveTotalPages(paginationMeta, EXPORT_PAGE_SIZE)
        if (!recordsData.length) break
        pageNum++
      }

      if (pageNum > MAX_PAGES) {
        message.warning('已達匯出頁數上限，請縮小日期區間後再試')
      }

      const table = checkoutOrdersToCsvRows(allOrders)
      const csvContent =
        '\uFEFF' +
        table.map((r) => (Array.isArray(r) ? r.join(',') : r)).join('\r\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const stamp = tzNow().format('YYYYMMDDHHmm')
      a.download = `結帳明細_${stamp}.csv`
      a.click()
      URL.revokeObjectURL(url)
      message.success('已匯出 CSV')
    } catch (err) {
      console.error('匯出 CSV 失敗', err)
      message.error(
        err?.response?.data?.message || err?.message || '匯出失敗，請稍後再試'
      )
    } finally {
      setExportCsvLoading(false)
    }
  }, [user?.businessId, hasCheckoutAccess, sortConfig])

  const handlePageChange = useCallback(
    (page, pageSize) => {
      const filtersWithSort = {
        ...filtersRef.current,
        sortField: sortConfig.sortField,
        sortOrder: sortConfig.sortOrder
      }
      fetchRecords(page, pageSize, filtersWithSort)
      const f = filtersRef.current
      syncParamsToUrl({
        page,
        pageSize,
        keyword: f.keyword,
        orderId: f.orderId,
        dateStart: f.dateRange?.[0]?.format('YYYY-MM-DD'),
        dateEnd: f.dateRange?.[1]?.format('YYYY-MM-DD'),
        personnelId: f.personnelId,
        status: f.status,
        paymentMethod: f.paymentMethod,
        hasPhoto: f.hasPhoto ? 1 : undefined,
        amountMin: f.minAmount,
        amountMax: f.maxAmount,
        sort: `${sortConfig.sortField}-${sortConfig.sortOrder}`
      })
    },
    [fetchRecords, sortConfig, syncParamsToUrl]
  )

  // 處理排序變更
  const handleSortChange = useCallback(
    (value) => {
      let newSortConfig
      switch (value) {
        case 'time.date-desc':
          newSortConfig = { sortField: 'time.date', sortOrder: 'desc' }
          break
        case 'time.date-asc':
          newSortConfig = { sortField: 'time.date', sortOrder: 'asc' }
          break
        case 'createdAt-desc':
          newSortConfig = { sortField: 'createdAt', sortOrder: 'desc' }
          break
        case 'orderId-asc':
          newSortConfig = { sortField: 'orderId', sortOrder: 'asc' }
          break
        case 'amounts.receivable-desc':
          newSortConfig = {
            sortField: 'amounts.receivable',
            sortOrder: 'desc'
          }
          break
        default:
          newSortConfig = { sortField: 'time.date', sortOrder: 'desc' }
      }
      setSortConfig(newSortConfig)
      // 更新 filtersRef 並重新查詢
      const updatedFilters = {
        ...filtersRef.current,
        sortField: newSortConfig.sortField,
        sortOrder: newSortConfig.sortOrder
      }
      filtersRef.current = updatedFilters
      fetchRecords(pagination.current, pagination.pageSize, updatedFilters)
      const f = filtersRef.current
      syncParamsToUrl({
        page: pagination.current,
        pageSize: pagination.pageSize,
        keyword: f.keyword,
        orderId: f.orderId,
        dateStart: f.dateRange?.[0]?.format('YYYY-MM-DD'),
        dateEnd: f.dateRange?.[1]?.format('YYYY-MM-DD'),
        personnelId: f.personnelId,
        status: f.status,
        paymentMethod: f.paymentMethod,
        hasPhoto: f.hasPhoto ? 1 : undefined,
        amountMin: f.minAmount,
        amountMax: f.maxAmount,
        sort: `${newSortConfig.sortField}-${newSortConfig.sortOrder}`
      })
    },
    [fetchRecords, pagination.current, pagination.pageSize, syncParamsToUrl]
  )

  // 處理移動端日期選擇器
  const handleMobileStartConfirm = (date) => {
    const newDate = dayjs(date)
    const currentRange = form.getFieldValue('dateRange')
    const end = currentRange?.[1] || newDate
    form.setFieldsValue({ dateRange: [newDate, end] })
    setShowMobileStartPicker(false)
  }

  const handleMobileEndConfirm = (date) => {
    const newDate = dayjs(date)
    const currentRange = form.getFieldValue('dateRange')
    const start = currentRange?.[0] || newDate
    form.setFieldsValue({ dateRange: [start, newDate] })
    setShowMobileEndPicker(false)
  }

  // 取消訂單交易
  // opts.silent: true → 不彈成功訊息（給「重新結帳」流程用，由呼叫端統一發訊息避免重複）
  // opts.reason: 自訂作廢原因（預設「取消交易」）
  const handleCancelOrder = async (orderId, bookingId, opts = {}) => {
    try {
      await api.post(
        `/api/businesses/${user.businessId}/checkout/orders/${orderId}/void`,
        {
          reason: opts.reason || '取消交易',
          bookingId: bookingId || null
        }
      )
      if (!opts.silent) {
        // 修：原本訊息寫「訂單已取消，正在重新結帳」會誤導純取消的用戶以為要跳轉。
        // 把訊息拆開：純取消顯示作廢成功 + 退款提醒；重新結帳由呼叫端發自己的訊息。
        message.success('訂單已作廢，已使用的儲值金將自動退回客戶帳戶')
      }
      // 重新獲取記錄列表
      await fetchRecords(
        pagination.current,
        pagination.pageSize,
        filtersRef.current
      )
    } catch (error) {
      console.error('取消訂單失敗:', error)
      message.error(error.response?.data?.message || '取消訂單失敗，請稍後再試')
      throw error
    }
  }

  // 重新結帳（優先傳遞 orderId，結帳頁會從訂單詳情載入完整內容：服務、商品、客戶、付款方式等）
  const handleRecheckout = async (record) => {
    const orderId = record.orderId || record._id
    const bookingId = record.bookingId || record.booking?._id

    Modal.confirm({
      title: '重新結帳',
      content:
        '此操作將取消原訂單並重新結帳，原訂單的消費內容（服務、商品、客戶、付款方式等）將自動帶入結帳頁面。確定繼續嗎？',
      okText: '確認',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          if (orderId) {
            // silent=true：避免顯示「已作廢」訊息，由本流程統一顯示「正在重新結帳」
            await handleCancelOrder(orderId, bookingId, {
              silent: true,
              reason: '重新結帳'
            })
          }
          message.success('原訂單已作廢，正在帶入結帳頁...')
          // 導航到結帳頁面：優先傳遞 orderId，讓結帳頁從訂單詳情載入完整內容
          const params = new URLSearchParams({ recheckout: 'true' })
          if (orderId) params.set('orderId', orderId)
          if (bookingId) params.set('bookingId', bookingId)
          navigate(`/checkout/checkout?${params.toString()}`)
        } catch (error) {
          message.error('操作失敗')
        }
      }
    })
  }

  // 僅取消交易
  const handleCancelOnly = async (record) => {
    const orderId = record.orderId || record._id
    const bookingId = record.bookingId || record.booking?._id

    Modal.confirm({
      title: '確認取消交易',
      content: `確定要取消訂單 ${orderId} 的交易嗎？此操作將作廢該訂單。`,
      okText: '確認',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await handleCancelOrder(orderId, bookingId)
        } catch (error) {
          // 错误已在 handleCancelOrder 中处理
        }
      }
    })
  }

  // 打開改日期 Modal
  const handleUpdateDate = (record) => {
    const orderId = record.orderId || record._id
    if (!orderId) return

    // 設置表單初始值為當前日期和時間
    const currentDate = record.time?.date ? dayjs(record.time.date) : tzNow()
    const currentStartTime = record.time?.startTime || '09:00'
    const currentEndTime = record.time?.endTime || null

    updateDateForm.setFieldsValue({
      date: currentDate,
      startTime: currentStartTime,
      endTime: currentEndTime
    })

    setSelectedRecordForDateUpdate(record)
    setIsUpdateDateModalVisible(true)
  }

  // 打開改備註 Modal
  const handleUpdateRemarks = (record) => {
    const orderId = record.orderId || record._id
    if (!orderId) return

    updateRemarksForm.setFieldsValue({
      remarks: record.remarks ?? ''
    })
    setSelectedRecordForRemarksUpdate(record)
    setIsUpdateRemarksModalVisible(true)
  }

  // 提交改備註
  const handleUpdateRemarksSubmit = async () => {
    try {
      const values = await updateRemarksForm.validateFields()
      if (!selectedRecordForRemarksUpdate) return

      const orderId =
        selectedRecordForRemarksUpdate.orderId ||
        selectedRecordForRemarksUpdate._id
      if (!orderId) return

      const payload = {
        remarks: values.remarks?.trim() ?? ''
      }

      await api.patch(
        `/api/businesses/${user.businessId}/checkout/orders/${orderId}/remarks`,
        payload
      )

      message.success('備註更新成功')
      setIsUpdateRemarksModalVisible(false)
      updateRemarksForm.resetFields()
      setSelectedRecordForRemarksUpdate(null)

      await fetchRecords(
        pagination.current,
        pagination.pageSize,
        filtersRef.current
      )
    } catch (error) {
      console.error('更新訂單備註失敗:', error)
      message.error(
        error.response?.data?.message || '更新訂單備註失敗，請稍後再試'
      )
    }
  }

  // 提交改日期
  const handleUpdateDateSubmit = async () => {
    try {
      const values = await updateDateForm.validateFields()
      if (!selectedRecordForDateUpdate) return

      const orderId =
        selectedRecordForDateUpdate.orderId || selectedRecordForDateUpdate._id
      if (!orderId) return

      const formattedDate = values.date.format('YYYY-MM-DD')
      const startTime = values.startTime || values.date.format('HH:mm')
      const endTime = values.endTime

      const payload = {
        date: formattedDate,
        startTime: startTime
      }

      if (endTime) {
        payload.endTime = endTime
      }

      await api.patch(
        `/api/businesses/${user.businessId}/checkout/orders/${orderId}/date`,
        payload
      )

      message.success('訂單日期更新成功')
      setIsUpdateDateModalVisible(false)
      updateDateForm.resetFields()
      setSelectedRecordForDateUpdate(null)

      // 重新獲取記錄列表
      await fetchRecords(
        pagination.current,
        pagination.pageSize,
        filtersRef.current
      )
    } catch (error) {
      console.error('更新訂單日期失敗:', error)
      message.error(
        error.response?.data?.message || '更新訂單日期失敗，請稍後再試'
      )
    }
  }

  // 所有 useEffect 必須在權限檢查之前
  useEffect(() => {
    if (!hasCheckoutAccess) return
    fetchEmployees()
  }, [fetchEmployees, hasCheckoutAccess])

  /**
   * 施作照片縮圖：records 換頁後，把這一頁所有關聯到預約的訂單
   * 用一支批次 API 撈回照片摘要。
   *
   * 刻意在父層撈而不是讓每張卡片自己去要 —— 一頁十筆就是十個往返，
   * 翻頁還會再來一次，對後端與店家的網路都不划算。
   */
  const servicePhotoEnabled =
    hasFeature('servicePhoto') &&
    canAny(['servicePhoto:view', 'servicePhoto:manage'])

  useEffect(() => {
    if (!servicePhotoEnabled || !user?.businessId) {
      setServicePhotoMap({})
      return
    }
    const bookingIds = [
      ...new Set(
        records
          .map((r) => r.bookingId || r.booking?._id)
          .filter(Boolean)
          .map(String)
      )
    ]
    if (bookingIds.length === 0) {
      setServicePhotoMap({})
      return
    }

    let alive = true
    listServicePhotosByBookings(user.businessId, bookingIds)
      .then((res) => {
        if (alive) setServicePhotoMap(res?.data?.data || {})
      })
      .catch(() => {
        // 靜默：照片撈不到只是少了縮圖，不該讓結帳紀錄跳錯誤
        if (alive) setServicePhotoMap({})
      })
    return () => {
      alive = false
    }
  }, [records, servicePhotoEnabled, user?.businessId])

  // 從 URL 還原搜尋與分頁（含返回上一頁）；無參數時使用默認查詢
  useEffect(() => {
    if (!hasCheckoutAccess) return
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false
      return
    }
    const page = parseInt(searchParams.get('page'), 10)
    const pageSize = parseInt(searchParams.get('pageSize'), 10)
    const keyword = searchParams.get('keyword') || ''
    const orderId = searchParams.get('orderId') || ''
    const dateStart = searchParams.get('dateStart') || ''
    const dateEnd = searchParams.get('dateEnd') || ''
    const personnelId = searchParams.get('personnelId') || undefined
    const status = searchParams.get('status') || undefined
    const paymentMethod = searchParams.get('paymentMethod') || undefined
    const hasPhoto = searchParams.get('hasPhoto') === '1'
    const amountMin = searchParams.get('amountMin')
    const amountMax = searchParams.get('amountMax')
    const sortVal = searchParams.get('sort') || 'time.date-desc'

    const hasUrlParams =
      (page >= 1 && page !== 1) ||
      (pageSize >= 1 && pageSize !== 10) ||
      keyword ||
      orderId ||
      dateStart ||
      dateEnd ||
      personnelId ||
      status ||
      paymentMethod ||
      hasPhoto ||
      amountMin ||
      amountMax ||
      sortVal !== 'time.date-desc'

    const [sortField, sortOrder] = sortVal.includes('-')
      ? sortVal.split('-')
      : ['time.date', 'desc']
    const filters = {
      keyword: keyword || undefined,
      orderId: orderId || undefined,
      personnelId: personnelId || undefined,
      status: status || undefined,
      paymentMethod: paymentMethod || undefined,
      hasPhoto,
      dateRange:
        dateStart && dateEnd ? [dayjs(dateStart), dayjs(dateEnd)] : undefined,
      minAmount:
        amountMin !== null && amountMin !== undefined && amountMin !== ''
          ? Number(amountMin)
          : undefined,
      maxAmount:
        amountMax !== null && amountMax !== undefined && amountMax !== ''
          ? Number(amountMax)
          : undefined,
      sortField,
      sortOrder
    }
    filtersRef.current = filters
    setSortConfig({ sortField, sortOrder })

    form.setFieldsValue({
      keyword: keyword || undefined,
      orderId: orderId || undefined,
      personnelId: personnelId || undefined,
      status: status || undefined,
      paymentMethod: paymentMethod || undefined,
      hasPhoto,
      minAmount:
        amountMin != null && amountMin !== '' ? Number(amountMin) : undefined,
      maxAmount:
        amountMax != null && amountMax !== '' ? Number(amountMax) : undefined,
      dateRange: filters.dateRange
    })

    const p = page >= 1 ? page : 1
    const ps = pageSize >= 1 ? pageSize : 10
    setPagination((prev) => ({ ...prev, current: p, pageSize: ps }))
    fetchRecords(p, ps, filters)
  }, [searchParams, hasCheckoutAccess])

  // 檢測移動設備
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const statusOptions = useMemo(
    () => [
      { value: 'paid', label: '已付款' },
      { value: 'voided', label: '已作廢' }
    ],
    []
  )

  // 權限檢查
  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <PageContainer>
          <div style={{ textAlign: 'center', padding: '100px' }}>
            <Spin size='large' tip='正在載入權限...' />
          </div>
        </PageContainer>
      </PageWrapper>
    )
  }

  if (!hasCheckoutAccess) {
    return (
      <PageWrapper>
        <PageContainer>
          <Result
            status='403'
            title='功能限制'
            subTitle='此功能需要更高級別的會員方案才能使用，請升級您的會員方案。'
            extra={
              <Button
                type='primary'
                onClick={() => navigate('/settings/membership')}
              >
                查看會員方案
              </Button>
            }
          />
        </PageContainer>
      </PageWrapper>
    )
  }

  const renderServices = (services = [], commissions = null) => {
    if (!services.length) {
      return <span>尚無服務紀錄</span>
    }

    // 處理第一劑/第二劑：將相同 serviceId 的第一劑和第二劑合併
    const processedServices = []
    const serviceMap = new Map()

    services.forEach((service) => {
      // 檢查是否為第一劑/第二劑模式
      const isDoseType =
        service.doseType ||
        service.firstDosePersonnelId ||
        service.secondDosePersonnelId

      if (isDoseType && service.serviceId) {
        // 第一劑/第二劑模式：按 serviceId 分組
        const key = service.serviceId
        if (!serviceMap.has(key)) {
          serviceMap.set(key, {
            serviceId: service.serviceId,
            name: service.name || service.serviceName || '服務項目',
            price: service.price,
            originalPrice: service.originalPrice,
            revenueAmount: service.revenueAmount,
            performanceAmount: service.performanceAmount,
            firstDosePersonnelId: null,
            firstDosePersonnelName: null,
            secondDosePersonnelId: null,
            secondDosePersonnelName: null,
            designerId: null,
            designerName: null,
            doseType: null
          })
        }

        const grouped = serviceMap.get(key)

        // 從 commissions 中提取設計師ID（如果 services 中沒有）
        if (!grouped.designerId && commissions?.services) {
          const commissionService = commissions.services.find(
            (cs) =>
              cs.serviceId === service.serviceId &&
              (cs.doseType === service.doseType ||
                (!cs.doseType && !service.doseType))
          )
          if (commissionService?.designerId) {
            grouped.designerId = commissionService.designerId
            // 嘗試從 employees 中獲取名稱
            const designer = employees.find(
              (e) => e._id === commissionService.designerId
            )
            if (designer) {
              grouped.designerName = designer.name
            }
          }
        }

        // 提取設計師ID（從 designerId 或 personnelId）
        const serviceDesignerId =
          service.designerId ||
          (typeof service.personnelId === 'object' &&
          service.personnelId !== null
            ? service.personnelId._id
            : service.personnelId) ||
          null
        const serviceDesignerName =
          service.designerName ||
          service.personnelName ||
          (typeof service.personnelId === 'object' &&
          service.personnelId !== null
            ? service.personnelId.name
            : null) ||
          null

        // 如果還沒有設置設計師，或者當前服務有設計師且與已設置的不同，則更新
        if (serviceDesignerId && !grouped.designerId) {
          grouped.designerId = serviceDesignerId
          grouped.designerName = serviceDesignerName
        }

        // 同時檢查並設置第一劑和第二劑信息（無論 doseType 為何）
        // 第一劑人員
        if (service.firstDosePersonnelId) {
          grouped.firstDosePersonnelId = service.firstDosePersonnelId
          grouped.firstDosePersonnelName =
            service.firstDosePersonnelName ||
            service.designerName ||
            service.personnelName ||
            null
        } else if (
          service.doseType === 'first' &&
          (service.designerId || service.personnelId)
        ) {
          // 如果 doseType 是 first 但沒有 firstDosePersonnelId，使用 designerId 或 personnelId
          grouped.firstDosePersonnelId =
            service.designerId ||
            (typeof service.personnelId === 'object' &&
            service.personnelId !== null
              ? service.personnelId._id
              : service.personnelId) ||
            null
          grouped.firstDosePersonnelName =
            service.designerName ||
            service.personnelName ||
            (typeof service.personnelId === 'object' &&
            service.personnelId !== null
              ? service.personnelId.name
              : null) ||
            null
        }

        // 第二劑人員
        if (service.secondDosePersonnelId) {
          grouped.secondDosePersonnelId = service.secondDosePersonnelId
          grouped.secondDosePersonnelName =
            service.secondDosePersonnelName ||
            service.designerName ||
            service.personnelName ||
            null
        } else if (
          service.doseType === 'second' &&
          (service.designerId || service.personnelId) &&
          !grouped.firstDosePersonnelId
        ) {
          // 如果 doseType 是 second 但沒有 secondDosePersonnelId，且第一劑還沒設置，使用 designerId 或 personnelId
          grouped.secondDosePersonnelId =
            service.designerId ||
            (typeof service.personnelId === 'object' &&
            service.personnelId !== null
              ? service.personnelId._id
              : service.personnelId) ||
            null
          grouped.secondDosePersonnelName =
            service.designerName ||
            service.personnelName ||
            (typeof service.personnelId === 'object' &&
            service.personnelId !== null
              ? service.personnelId.name
              : null) ||
            null
        }

        // 如果價格相同，使用第一個的價格
        if (!grouped.price && service.price) {
          grouped.price = service.price
        }
        if (
          (grouped.quantity === undefined || grouped.quantity === null) &&
          service.quantity !== undefined &&
          service.quantity !== null
        ) {
          grouped.quantity = service.quantity
        }
        if (!grouped.originalPrice && service.originalPrice) {
          grouped.originalPrice = service.originalPrice
        }
        // 合併業績金額（如果有多個，取第一個非空值）
        if (
          !grouped.revenueAmount &&
          service.revenueAmount !== undefined &&
          service.revenueAmount !== null
        ) {
          grouped.revenueAmount = service.revenueAmount
        }
        if (
          (grouped.performanceAmount === undefined ||
            grouped.performanceAmount === null) &&
          service.performanceAmount !== undefined &&
          service.performanceAmount !== null
        ) {
          grouped.performanceAmount = service.performanceAmount
        }
      } else {
        // 一般抽成模式：直接添加
        processedServices.push(service)
      }
    })

    // 將合併後的第一劑/第二劑服務添加到列表
    serviceMap.forEach((grouped) => {
      processedServices.push(grouped)
    })

    return (
      <ServicesList>
        {processedServices.map((service, index) => {
          // 檢查是否為第一劑/第二劑模式
          const isDoseType =
            service.firstDosePersonnelId || service.secondDosePersonnelId

          if (isDoseType) {
            // 第一劑/第二劑模式顯示 - 顯示所有可用的人員信息
            const personnel = []

            // 第一劑人員（無論 doseType 為何，只要有值就顯示）
            if (
              service.firstDosePersonnelId ||
              service.firstDosePersonnelName
            ) {
              const firstDoseName =
                service.firstDosePersonnelName ||
                (service.firstDosePersonnelId
                  ? employees.find(
                      (e) => e._id === service.firstDosePersonnelId
                    )?.name
                  : null) ||
                null
              if (firstDoseName) {
                personnel.push({ type: '第一劑', name: firstDoseName })
              }
            }

            // 第二劑人員（無論 doseType 為何，只要有值就顯示）
            if (
              service.secondDosePersonnelId ||
              service.secondDosePersonnelName
            ) {
              const secondDoseName =
                service.secondDosePersonnelName ||
                (service.secondDosePersonnelId
                  ? employees.find(
                      (e) => e._id === service.secondDosePersonnelId
                    )?.name
                  : null) ||
                null
              if (secondDoseName) {
                personnel.push({ type: '第二劑', name: secondDoseName })
              }
            }

            // 設計師（一二劑模式也要顯示設計師）
            if (service.designerId) {
              const designerName =
                service.designerName ||
                employees.find((e) => e._id === service.designerId)?.name ||
                null
              if (designerName) {
                personnel.push({ type: '設計師', name: designerName })
              }
            }

            // 助理
            if (service.assistantId) {
              const assistantName =
                service.assistantName ||
                employees.find((e) => e._id === service.assistantId)?.name ||
                null
              if (assistantName) {
                personnel.push({ type: '助理', name: assistantName })
              }
            }

            return (
              <li key={`${service.serviceId || index}-${index}`}>
                <div style={{ marginBottom: '4px' }}>
                  <strong>
                    {service.name || service.serviceName || '服務項目'}
                  </strong>
                  {(service.price !== undefined ||
                    service.originalPrice !== undefined) && (
                    <span>
                      {(() => {
                        const qty = Number(service.quantity) || 1
                        // API 約定：price、originalPrice 一律為單價，該行總價 = price × quantity
                        const unitPrice =
                          service.price !== undefined && service.price !== null
                            ? Number(service.price)
                            : Number(service.originalPrice) || 0
                        const lineTotal = unitPrice * qty
                        const showQty = qty > 1
                        if (showQty) {
                          return `${formatCurrency(unitPrice)} x${qty} = ${formatCurrency(lineTotal)}`
                        }
                        return formatCurrency(unitPrice)
                      })()}
                    </span>
                  )}
                </div>
                {(() => {
                  // 該行業績：優先 performanceAmount（總額），否則 revenueAmount × quantity（單價×數量）
                  const qty = Number(service.quantity) || 1
                  const amt =
                    service.performanceAmount ??
                    (service.revenueAmount != null
                      ? Number(service.revenueAmount) * qty
                      : null)
                  const amtNum = amt != null ? Number(amt) : null
                  const unitPrice =
                    service.price !== undefined && service.price !== null
                      ? Number(service.price)
                      : Number(service.originalPrice) || 0
                  const lineTotal = unitPrice * qty
                  return (
                    amtNum != null &&
                    Number(amtNum) !== 0 &&
                    Number(amtNum) !== lineTotal && (
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#8c4a00',
                          marginTop: '4px',
                          paddingLeft: '8px',
                          fontWeight: 500
                        }}
                      >
                        應用業績：{formatCurrency(amtNum)}
                      </div>
                    )
                  )
                })()}
                {personnel.length > 0 && (
                  <div
                    style={{
                      fontSize: '13px',
                      color: '#576079',
                      marginTop: '6px',
                      paddingLeft: '8px',
                      lineHeight: '1.5'
                    }}
                  >
                    {personnel.map((p, pIdx) => (
                      <span
                        key={pIdx}
                        style={{
                          marginRight: '16px',
                          display: 'inline-block'
                        }}
                      >
                        <strong style={{ color: '#0f2562' }}>{p.type}：</strong>
                        {p.name}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            )
          } else {
            // 一般抽成模式顯示 - 顯示所有可用的人員信息
            const personnel = []

            // 處理 personnelId 可能是對象或字符串的情況
            const personnelIdValue =
              typeof service.personnelId === 'object' &&
              service.personnelId !== null
                ? service.personnelId._id
                : service.personnelId

            // 設計師
            const designerId = service.designerId || personnelIdValue || null
            const designer = designerId
              ? employees.find((e) => e._id === designerId)
              : null
            const personnelNameValue =
              typeof service.personnelId === 'object' &&
              service.personnelId !== null
                ? service.personnelId.name
                : null
            const designerName =
              service.designerName ||
              service.personnelName ||
              personnelNameValue ||
              designer?.name ||
              null
            if (designerName) {
              personnel.push({ type: '設計師', name: designerName })
            }

            // 助理
            const assistantId = service.assistantId || null
            const assistant = assistantId
              ? employees.find((e) => e._id === assistantId)
              : null
            const assistantName =
              service.assistantName || assistant?.name || null
            if (assistantName) {
              personnel.push({ type: '助理', name: assistantName })
            }

            // 第一劑人員（無論 doseType 為何，只要有值就顯示）
            if (service.firstDosePersonnelId) {
              const firstDoseName =
                service.firstDosePersonnelName ||
                (service.firstDosePersonnelId
                  ? employees.find(
                      (e) => e._id === service.firstDosePersonnelId
                    )?.name
                  : null) ||
                null
              if (firstDoseName) {
                personnel.push({ type: '第一劑', name: firstDoseName })
              }
            }

            // 第二劑人員（無論 doseType 為何，只要有值就顯示）
            if (service.secondDosePersonnelId) {
              const secondDoseName =
                service.secondDosePersonnelName ||
                (service.secondDosePersonnelId
                  ? employees.find(
                      (e) => e._id === service.secondDosePersonnelId
                    )?.name
                  : null) ||
                null
              if (secondDoseName) {
                personnel.push({ type: '第二劑', name: secondDoseName })
              }
            }

            return (
              <li key={`${service.serviceId || index}-${index}`}>
                <div style={{ marginBottom: '4px' }}>
                  <strong>
                    {service.name || service.serviceName || '服務項目'}
                  </strong>
                  {(service.price !== undefined ||
                    service.originalPrice !== undefined) && (
                    <span>
                      {(() => {
                        const qty2 = Number(service.quantity) || 1
                        // API 約定：price、originalPrice 一律為單價，該行總價 = price × quantity
                        const unitPrice2 =
                          service.price !== undefined && service.price !== null
                            ? Number(service.price)
                            : Number(service.originalPrice) || 0
                        const lineTotal2 = unitPrice2 * qty2
                        const showQty2 = qty2 > 1
                        if (showQty2) {
                          return `${formatCurrency(unitPrice2)} x${qty2} = ${formatCurrency(lineTotal2)}`
                        }
                        return formatCurrency(unitPrice2)
                      })()}
                    </span>
                  )}
                </div>
                {(() => {
                  // 該行業績：優先 performanceAmount（總額），否則 revenueAmount × quantity（單價×數量）
                  const qty2 = Number(service.quantity) || 1
                  const amt =
                    service.performanceAmount ??
                    (service.revenueAmount != null
                      ? Number(service.revenueAmount) * qty2
                      : null)
                  const amtNum = amt != null ? Number(amt) : null
                  const unitPrice2 =
                    service.price !== undefined && service.price !== null
                      ? Number(service.price)
                      : Number(service.originalPrice) || 0
                  const lineTotal2 = unitPrice2 * qty2
                  return (
                    amtNum != null &&
                    Number(amtNum) !== 0 &&
                    Number(amtNum) !== lineTotal2 && (
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#8c4a00',
                          marginTop: '4px',
                          paddingLeft: '8px',
                          fontWeight: 500
                        }}
                      >
                        應用業績：{formatCurrency(amtNum)}
                      </div>
                    )
                  )
                })()}
                {personnel.length > 0 && (
                  <div
                    style={{
                      fontSize: '13px',
                      color: '#576079',
                      marginTop: '6px',
                      paddingLeft: '8px',
                      lineHeight: '1.5'
                    }}
                  >
                    {personnel.map((p, pIdx) => (
                      <span
                        key={pIdx}
                        style={{
                          marginRight: '16px',
                          display: 'inline-block'
                        }}
                      >
                        <strong style={{ color: '#0f2562' }}>{p.type}：</strong>
                        {p.name}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            )
          }
        })}
      </ServicesList>
    )
  }

  const renderProductLine = (product, index) => {
    // 該行業績：優先 performanceAmount（總額），否則 revenueAmount × quantity（單價×數量）
    const qty = Number(product.quantity) || 1
    const amt =
      product.performanceAmount ??
      (product.revenueAmount != null
        ? Number(product.revenueAmount) * qty
        : null)
    const amtNum = amt != null ? Number(amt) : null
    return (
      <li key={`${product.productId || index}-${index}`}>
        <div style={{ marginBottom: '4px' }}>
          <strong>{product.name || product.productName || '商品'}</strong>
          {product.quantity !== undefined && (
            <span> × {product.quantity}</span>
          )}
          {product.price !== undefined && (
            <span> - {formatCurrency(product.price)}</span>
          )}
          {product.totalPrice !== undefined && (
            <span> (小計: {formatCurrency(product.totalPrice)})</span>
          )}
          {product.isConsumable && (
            <Tag
              color='default'
              style={{ marginLeft: 8, fontSize: 11, lineHeight: '18px' }}
            >
              耗材
            </Tag>
          )}
        </div>
        {amtNum != null &&
          Number(amtNum) !== 0 &&
          Number(amtNum) !== Number(product.totalPrice) && (
            <div
              style={{
                fontSize: '13px',
                color: '#8c4a00',
                marginTop: '4px',
                paddingLeft: '8px',
                fontWeight: 500
              }}
            >
              商品業績：{formatCurrency(amtNum)}
            </div>
          )}
        {product.productSalesPersonnelName && (
          <div
            style={{
              fontSize: '13px',
              color: '#576079',
              marginTop: '6px',
              paddingLeft: '8px',
              lineHeight: '1.5'
            }}
          >
            <span>
              <strong style={{ color: '#0f2562' }}>銷售人員：</strong>
              {product.productSalesPersonnelName}
            </span>
          </div>
        )}
      </li>
    )
  }

  // 商品明細小元件：含「顯示／隱藏耗材」切換。
  // 預設摺疊耗材，方便店家直接截圖給客人看。
  const ProductsSection = ({ products = [] }) => {
    const [showConsumables, setShowConsumables] = useState(false)
    if (!products.length) {
      return <span>尚無商品紀錄</span>
    }
    const consumables = products.filter((p) => p && p.isConsumable)
    const regulars = products.filter((p) => !(p && p.isConsumable))
    const visible = showConsumables ? products : regulars

    return (
      <ServicesList>
        {visible.map((product, index) => renderProductLine(product, index))}
        {regulars.length === 0 && consumables.length > 0 && !showConsumables && (
          <li
            style={{
              listStyle: 'none',
              color: '#888',
              fontSize: 13,
              paddingLeft: 0
            }}
          >
            本次共 {consumables.length} 項耗材，已隱藏
          </li>
        )}
        {consumables.length > 0 && (
          <li
            style={{
              listStyle: 'none',
              paddingLeft: 0,
              marginTop: 4,
              textAlign: 'right'
            }}
          >
            <Button
              type='link'
              size='small'
              icon={
                showConsumables ? <EyeInvisibleOutlined /> : <EyeOutlined />
              }
              onClick={() => setShowConsumables((v) => !v)}
            >
              {showConsumables
                ? '隱藏耗材'
                : `顯示耗材（${consumables.length}）`}
            </Button>
          </li>
        )}
      </ServicesList>
    )
  }

  const renderPaymentBreakdown = (payment = {}) => {
    const entries = paymentMethodLabels
      .map((item) => ({
        key: item.value,
        label: item.label,
        amount: Number(payment[item.value]) || 0
      }))
      .filter((entry) => entry.amount > 0)
      .map((entry) => {
        if (entry.key !== 'deposit') return entry
        const method = DEPOSIT_METHOD_LABELS[payment.depositMethod]
        /**
         * 定金常常是消費日的好幾天前就收了（跨月更常見），
         * 單子上只寫「定金 $500」會看不出這筆錢是哪天進來的，所以標上收款日期。
         * 舊訂單沒有這個欄位時就不顯示（後端會盡量用關聯預約補）。
         */
        const paidAtText = payment.depositPaidAt
          ? formatTz(payment.depositPaidAt, 'YYYY/M/D', '')
          : ''
        return {
          ...entry,
          label: method ? `${entry.label}（${method}）` : entry.label,
          note: paidAtText
        }
      })

    // 金流手續費（店家支出，非客戶付款）：單獨顯示，不計入付款方式合計
    const paymentFee = Number(payment.paymentFee) || 0
    const cardFeeRate = Number(payment.cardFeeRate) || 0
    const mobileFeeRate = Number(payment.mobilePaymentFeeRate) || 0
    const feeRateNote =
      cardFeeRate > 0 || mobileFeeRate > 0
        ? `（刷卡 ${cardFeeRate}%${mobileFeeRate > 0 ? ` / 行動支付 ${mobileFeeRate}%` : ''}）`
        : ''

    if (!entries.length && paymentFee <= 0) {
      return <span>尚無付款紀錄</span>
    }

    return (
      <PaymentList>
        {entries.map((entry) => (
          <PaymentItem key={entry.key}>
            <span>
              {entry.label}
              {entry.note && <PaymentNote>{entry.note}</PaymentNote>}
            </span>
            <span>{formatCurrency(entry.amount)}</span>
          </PaymentItem>
        ))}
        {paymentFee > 0 && (
          <PaymentItem key='paymentFee' style={{ color: '#c0392b' }}>
            <span>金流手續費{feeRateNote}</span>
            <span>- {formatCurrency(paymentFee)}</span>
          </PaymentItem>
        )}
        {/* 定金刷卡的手續費已含在上面的合計裡，這裡標出來是因為它發生在
            結帳之前、金額來自預約那一端，對不上時最容易卡在這一筆 */}
        {Number(payment.depositFee) > 0 && (
          <PaymentItem
            key='depositFee'
            style={{ color: '#c0392b', opacity: 0.75, fontSize: '0.9em' }}
          >
            <span>　其中定金刷卡手續費</span>
            <span>{formatCurrency(payment.depositFee)}</span>
          </PaymentItem>
        )}
      </PaymentList>
    )
  }

  return (
    <>
      <PageWrapper>
        <PageContainer>
          <SearchCard>
            <Form form={form} layout='vertical'>
              <FilterRow>
                <Form.Item name='keyword' label='關鍵字（客戶、電話）'>
                  <Input
                    placeholder='輸入客戶姓名、電話或備註'
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Form.Item>
                <Form.Item name='orderId' label='訂單編號'>
                  <Input
                    placeholder='輸入訂單編號'
                    prefix={<FilterOutlined />}
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  name='dateRange'
                  label='結帳日期區間'
                  className='filter-span-full'
                >
                  {isMobile ? (
                    <div>
                      <MobileDateGrid>
                      <Button
                        block
                        icon={<CalendarOutlined />}
                        onClick={() => setShowMobileStartPicker(true)}
                        className='date-btn'
                      >
                        <span
                          className={
                            form.getFieldValue('dateRange')?.[0]
                              ? 'date-text'
                              : 'date-text placeholder'
                          }
                        >
                          {form.getFieldValue('dateRange')?.[0]
                            ? form
                                .getFieldValue('dateRange')[0]
                                .format('YYYY-MM-DD')
                            : '選擇開始日期'}
                        </span>
                        {form.getFieldValue('dateRange')?.[0] && (
                          <Button
                            type='text'
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation()
                              const currentRange =
                                form.getFieldValue('dateRange')
                              form.setFieldsValue({
                                dateRange: [null, currentRange?.[1] || null]
                              })
                            }}
                            style={{ padding: 0, height: 'auto' }}
                          >
                            清除
                          </Button>
                        )}
                      </Button>
                      <Button
                        block
                        icon={<CalendarOutlined />}
                        onClick={() => setShowMobileEndPicker(true)}
                        className='date-btn'
                      >
                        <span
                          className={
                            form.getFieldValue('dateRange')?.[1]
                              ? 'date-text'
                              : 'date-text placeholder'
                          }
                        >
                          {form.getFieldValue('dateRange')?.[1]
                            ? form
                                .getFieldValue('dateRange')[1]
                                .format('YYYY-MM-DD')
                            : '選擇結束日期'}
                        </span>
                        {form.getFieldValue('dateRange')?.[1] && (
                          <Button
                            type='text'
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation()
                              const currentRange =
                                form.getFieldValue('dateRange')
                              form.setFieldsValue({
                                dateRange: [currentRange?.[0] || null, null]
                              })
                            }}
                            style={{ padding: 0, height: 'auto' }}
                          >
                            清除
                          </Button>
                        )}
                      </Button>
                      </MobileDateGrid>

                      <MobileDatePicker
                        visible={showMobileStartPicker}
                        onClose={() => setShowMobileStartPicker(false)}
                        precision='day'
                        onConfirm={handleMobileStartConfirm}
                        max={new Date(2030, 11, 31)}
                        getContainer={null}
                      />

                      <MobileDatePicker
                        visible={showMobileEndPicker}
                        onClose={() => setShowMobileEndPicker(false)}
                        precision='day'
                        onConfirm={handleMobileEndConfirm}
                        min={
                          form.getFieldValue('dateRange')?.[0]
                            ? form.getFieldValue('dateRange')[0].toDate()
                            : undefined
                        }
                        max={new Date(2030, 11, 31)}
                        getContainer={null}
                      />
                    </div>
                  ) : (
                    <RangePicker
                      style={{ width: '100%' }}
                      placeholder={['開始日期', '結束日期']}
                      format='YYYY-MM-DD'
                      allowClear
                    />
                  )}
                </Form.Item>
              </FilterRow>

              {/* 排序選擇器 + 顯示已作廢勾選 */}
              <SortBar>
                <SortBarLabel>排序方式：</SortBarLabel>
                <SortBarSelect>
                  <Select
                    value={`${sortConfig.sortField}-${sortConfig.sortOrder}`}
                    onChange={handleSortChange}
                    style={{ width: '100%' }}
                    placeholder='選擇排序方式'
                  >
                    <Select.Option value='time.date-desc'>
                      按消費日期降序（預設，最新在前）
                    </Select.Option>
                    <Select.Option value='time.date-asc'>
                      按消費日期升序（最舊在前）
                    </Select.Option>
                    <Select.Option value='createdAt-desc'>
                      按結帳日期降序（最新在前）
                    </Select.Option>
                    <Select.Option value='orderId-asc'>
                      按訂單編號升序
                    </Select.Option>
                    <Select.Option value='amounts.receivable-desc'>
                      按應收金額降序（最高在前）
                    </Select.Option>
                  </Select>
                </SortBarSelect>
                <SortBarChecks>
                <SortBarCheckbox name='includeVoided' valuePropName='checked'>
                  <Checkbox
                    onChange={(e) => {
                      // 勾選變更時立即重新查詢（不需按「搜尋」）
                      const updated = {
                        ...filtersRef.current,
                        includeVoided: e.target.checked
                      }
                      filtersRef.current = updated
                      fetchRecords(1, pagination.pageSize, updated)
                      setPagination((prev) => ({ ...prev, current: 1 }))
                    }}
                  >顯示已作廢訂單</Checkbox>
                </SortBarCheckbox>
                {servicePhotoEnabled && (
                  <SortBarCheckbox name='hasPhoto' valuePropName='checked'>
                    <Checkbox
                      onChange={(e) => {
                        // 跟「顯示已作廢」一樣，勾了立即重查，不用再按搜尋
                        const updated = {
                          ...filtersRef.current,
                          hasPhoto: e.target.checked
                        }
                        filtersRef.current = updated
                        fetchRecords(1, pagination.pageSize, updated)
                        setPagination((prev) => ({ ...prev, current: 1 }))
                        syncParamsToUrl({
                          page: 1,
                          hasPhoto: e.target.checked ? 1 : undefined
                        })
                      }}
                    >
                      <CameraOutlined style={{ color: '#d5728a', marginRight: 4 }} />
                      只看有施作照片
                    </Checkbox>
                  </SortBarCheckbox>
                )}
                </SortBarChecks>
              </SortBar>

              <Collapse
                ghost
                style={{ marginTop: 16 }}
                items={[
                  {
                    key: 'advanced',
                    label: (
                      <CollapseHeader>
                        <FilterOutlined /> 更多過濾條件
                      </CollapseHeader>
                    ),
                    children: (
                      <>
                        <FilterRow>
                          <Form.Item name='personnelId' label='服務人員'>
                            <Select
                              placeholder='選擇服務人員'
                              allowClear
                              showSearch
                              optionFilterProp='children'
                            >
                              {employees.map((employee) => (
                                <Select.Option
                                  key={employee._id}
                                  value={employee._id}
                                >
                                  {employee.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item name='status' label='訂單狀態'>
                            <Select placeholder='選擇訂單狀態' allowClear>
                              {statusOptions.map((status) => (
                                <Select.Option
                                  key={status.value}
                                  value={status.value}
                                >
                                  {status.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item name='source' label='訂單來源'>
                            <Select placeholder='全部來源' allowClear>
                              {sourceOptions.map((item) => (
                                <Select.Option
                                  key={item.value}
                                  value={item.value}
                                >
                                  {item.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                          <Form.Item name='paymentMethod' label='付款方式'>
                            <Select placeholder='選擇付款方式' allowClear>
                              {paymentMethodLabels.map((method) => (
                                <Select.Option
                                  key={method.value}
                                  value={method.value}
                                >
                                  {method.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </FilterRow>

                        <FilterRow>
                          <Form.Item name='minAmount' label='最低應收金額'>
                            <Input
                              type='number'
                              min={0}
                              placeholder='輸入最低金額'
                              prefix={<DollarOutlined />}
                            />
                          </Form.Item>
                          <Form.Item name='maxAmount' label='最高應收金額'>
                            <Input
                              type='number'
                              min={0}
                              placeholder='輸入最高金額'
                              prefix={<DollarOutlined />}
                            />
                          </Form.Item>
                        </FilterRow>
                      </>
                    )
                  }
                ]}
              />

              <FilterActions>
                <Button
                  type='primary'
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  搜尋
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  重置
                </Button>
                <CsvExportButton
                  icon={<DownloadOutlined />}
                  loading={exportCsvLoading}
                  onClick={handleExportCheckoutCsv}
                >
                  匯出 CSV
                </CsvExportButton>
              </FilterActions>
            </Form>
          </SearchCard>

          <ResultWrapper>
            <Spin spinning={loading}>
              {records.length === 0 ? (
                <Empty description='目前沒有符合條件的結帳紀錄' />
              ) : (
                <CardGrid>
                  {records.map((record) => {
                    const recordId =
                      record.orderId || record._id || '未命名訂單'
                    const createdAt = record.createdAt || record.time?.date
                    const status = record.status || 'paid'
                    const amounts = record.amounts || {}
                    const payment = record.payment || {}
                    const services = record.services || record.items || []
                    const products = record.products || []
                    const classTickets = record.courses?.purchases || []
                    // 這張單用掉的堂票（折抵）。金額不計收入，只顯示扣了哪張、剩幾堂
                    const ticketRedemptions = record.classTicketUsages || []
                    const servicesAmount =
                      Number(amounts.servicesAmount) > 0
                        ? Number(amounts.servicesAmount)
                        : 0
                    const productsAmount =
                      Number(amounts.productsAmount) > 0
                        ? Number(amounts.productsAmount)
                        : 0
                    const coursesAmount =
                      Number(amounts.coursesAmount) > 0
                        ? Number(amounts.coursesAmount)
                        : classTickets.reduce(
                            (sum, c) =>
                              sum +
                              (Number(c.price) || 0) *
                                (Number(c.quantity) || 0),
                            0
                          )
                    const receivableAmount =
                      Number(amounts.receivable) > 0
                        ? Number(amounts.receivable)
                        : 0
                    const totalBeforeDiscount = servicesAmount + productsAmount
                    const discountAmount =
                      Number(amounts.discountAmount) > 0
                        ? Number(amounts.discountAmount)
                        : 0
                    // 向客人加收的服務費（已含在應收裡）。
                    // 名稱用訂單存的快照 —— 店家日後改名，舊單仍顯示當時給客人看到的字。
                    const surchargeAmount =
                      Number(amounts.surchargeAmount) > 0
                        ? Number(amounts.surchargeAmount)
                        : 0
                    const surchargeLabel = amounts.surchargeLabel || '服務費'
                    const couponDeduction = Number(payment.voucher) || 0
                    const appliedCoupon =
                      record.appliedStoreCoupon || record.appliedUserCoupon || null
                    // 施作照片掛在預約上，純賣商品（沒關聯預約）的訂單就沒有
                    const photoBookingId = String(
                      record.bookingId || record.booking?._id || ''
                    )
                    const servicePhotoEntry = photoBookingId
                      ? servicePhotoMap[photoBookingId]
                      : null

                    return (
                      <RecordCard key={recordId}>
                        <CardHeader>
                          <h3>{recordId}</h3>
                          <HeaderMeta>
                            {/* 來源標記：全部檢視時要分得出哪筆是線上訂單。
                                歷史訂單沒有 source 欄位，那些一律是櫃檯結帳，不標。 */}
                            {record.source && record.source !== 'checkout' && (
                              <StatusTag
                                color={
                                  record.source === 'shop_order'
                                    ? 'magenta'
                                    : 'purple'
                                }
                              >
                                {SOURCE_LABEL[record.source] || record.source}
                              </StatusTag>
                            )}
                            <StatusTag color={statusColors[status] || 'blue'}>
                              {statusOptions.find(
                                (item) => item.value === status
                              )?.label || '已付款'}
                            </StatusTag>
                            <span>
                              <CalendarOutlined />{' '}
                              {formatTz(
                                createdAt,
                                'YYYY-MM-DD HH:mm',
                                '--'
                              )}
                            </span>
                            {status === 'voided' && record.voidReason && (
                              <span
                                style={{
                                  fontSize: '12px',
                                  color: '#ff4d4f',
                                  marginTop: '4px',
                                  display: 'block'
                                }}
                              >
                                作廢原因：{record.voidReason}
                                {record.voidedAt && (
                                  <span
                                    style={{
                                      marginLeft: '8px',
                                      color: '#8c8c8c'
                                    }}
                                  >
                                    (
                                    {dayjs(record.voidedAt).format(
                                      'YYYY-MM-DD HH:mm'
                                    )}
                                    )
                                  </span>
                                )}
                              </span>
                            )}
                          </HeaderMeta>
                        </CardHeader>

                        <Section>
                          <SectionTitle>
                            <UserOutlined /> 客戶資訊
                          </SectionTitle>
                          <SectionContent>
                            <span>
                              姓名：
                              <strong>
                                <CustomerNameLink
                                  customerId={record.customer?.customerId}
                                >
                                  {record.customer?.name || '--'}
                                </CustomerNameLink>
                              </strong>
                            </span>
                            <span>
                              電話：
                              <strong>{record.customer?.phone || '--'}</strong>
                            </span>
                            <span>
                              消費日期：
                              <strong>{record.time?.date}</strong>
                            </span>
                            {record.customer?.servicePersonnel && (
                              <span>
                                服務人員：
                                <strong>
                                  {employees.find(
                                    (e) =>
                                      e._id ===
                                      record.customer?.servicePersonnel
                                  )?.name || record.customer?.servicePersonnel}
                                </strong>
                              </span>
                            )}
                          </SectionContent>
                        </Section>

                        {services.length > 0 && (
                          <Section>
                            <SectionTitle>
                              <CalendarOutlined /> 服務明細
                            </SectionTitle>
                            {renderServices(services, record.commissions)}
                          </Section>
                        )}

                        {products.length > 0 && (
                          <Section>
                            <SectionTitle>
                              <ShoppingOutlined /> 商品明細
                            </SectionTitle>
                            <ProductsSection products={products} />
                          </Section>
                        )}

                        {classTickets.length > 0 && (
                          <Section>
                            <SectionTitle>
                              <IdcardOutlined /> 堂票購買
                            </SectionTitle>
                            <SectionContent>
                              {classTickets.map((c, i) => (
                                <span key={i}>
                                  {c.courseName || '堂票'} × {c.quantity || 1}
                                  {' '}
                                  <strong>
                                    {formatCurrency(
                                      (Number(c.price) || 0) *
                                        (Number(c.quantity) || 0)
                                    )}
                                  </strong>
                                  {c.salesPersonnelName && (
                                    <span style={{ color: '#888', fontSize: 12, marginLeft: 6 }}>
                                      銷售：{c.salesPersonnelName}
                                    </span>
                                  )}
                                </span>
                              ))}
                            </SectionContent>
                          </Section>
                        )}

                        {ticketRedemptions.length > 0 && (
                          <Section>
                            <SectionTitle>
                              <IdcardOutlined /> 使用堂票（核銷）
                            </SectionTitle>
                            <SectionContent>
                              {ticketRedemptions.map((u, i) => (
                                <span key={i}>
                                  {u.serviceName || '服務'}
                                  {' — '}
                                  <strong>
                                    {u.templateName || '堂票'}
                                  </strong>
                                  {u.ticketCode ? `（${u.ticketCode}）` : ''}
                                  {' 扣 '}
                                  {u.count || 1}
                                  {' 堂'}
                                  {u.remainingAfter != null && (
                                    <span
                                      style={{
                                        color: '#888',
                                        fontSize: 12,
                                        marginLeft: 6
                                      }}
                                    >
                                      剩 {u.remainingAfter} 堂
                                    </span>
                                  )}
                                  {Number(u.deductedAmount) > 0 && (
                                    <span
                                      style={{
                                        color: '#888',
                                        fontSize: 12,
                                        marginLeft: 6
                                      }}
                                    >
                                      原價 {formatCurrency(u.deductedAmount)}
                                    </span>
                                  )}
                                </span>
                              ))}
                              <span style={{ color: '#888', fontSize: 12 }}>
                                堂票的收入在售票當天就已認列，核銷不重複計入收入與業績
                              </span>
                            </SectionContent>
                          </Section>
                        )}

                        {servicePhotoEntry?.photos?.length > 0 && (
                          <Section>
                            <SectionTitle>
                              <CameraOutlined /> 施作照片（
                              {servicePhotoEntry.total} 張）
                            </SectionTitle>
                            <ServicePhotoThumbs
                              businessId={user.businessId}
                              bookingId={photoBookingId}
                              photos={servicePhotoEntry.photos}
                              total={servicePhotoEntry.total}
                              showLabel={false}
                            />
                          </Section>
                        )}

                        <Section>
                          <SectionTitle>
                            <DollarOutlined /> 金額資訊
                          </SectionTitle>
                          <SectionContent>
                            {amounts.servicesAmount > 0 && (
                              <span>
                                服務小計：
                                <strong>
                                  {formatCurrency(amounts.servicesAmount || 0)}
                                </strong>
                              </span>
                            )}
                            {amounts.productsAmount > 0 && (
                              <span>
                                商品小計：
                                <strong>
                                  {formatCurrency(amounts.productsAmount || 0)}
                                </strong>
                              </span>
                            )}
                            {coursesAmount > 0 && (
                              <span>
                                堂票小計：
                                <strong>
                                  {formatCurrency(coursesAmount)}
                                </strong>
                              </span>
                            )}
                            {surchargeAmount > 0 && (
                              <span>
                                {surchargeLabel}：
                                <strong>
                                  {formatCurrency(surchargeAmount)}
                                </strong>
                              </span>
                            )}
                            {receivableAmount > 0 && (
                              <span>
                                應收金額：
                                <strong>
                                  {formatCurrency(receivableAmount)}
                                </strong>
                              </span>
                            )}
                            {discountAmount > 0 && (
                              <span>
                                折扣金額：
                                <strong>
                                  {formatCurrency(discountAmount)}
                                </strong>
                              </span>
                            )}
                            <span>
                              已付金額：
                              <strong>
                                {formatCurrency(amounts.paid || 0)}
                              </strong>
                            </span>
                            {amounts.unpaid !== undefined &&
                              amounts.unpaid > 0 && (
                                <span>
                                  未付金額：
                                  <strong>
                                    {formatCurrency(amounts.unpaid)}
                                  </strong>
                                </span>
                              )}
                          </SectionContent>
                        </Section>

                        {/* 分期：這張單有多少錢是分期收的、收回來多少。
                            資料就在訂單的 payment 裡，不用另外打 API。 */}
                        {Number(payment.installment) > 0 && (
                          <Section>
                            <SectionTitle>
                              <Tag color='purple' style={{ marginRight: 0 }}>
                                分期
                              </Tag>
                              分期收款
                            </SectionTitle>
                            <SectionContent>
                              <span>
                                分期總額：
                                <strong>
                                  {formatCurrency(payment.installment)}
                                </strong>
                              </span>
                              <span>
                                已收：
                                <strong>
                                  {formatCurrency(payment.installmentPaid || 0)}
                                </strong>
                              </span>
                              <span>
                                未收：
                                <strong>
                                  {formatCurrency(
                                    Math.max(
                                      0,
                                      Number(payment.installment) -
                                        Number(payment.installmentPaid || 0)
                                    )
                                  )}
                                </strong>
                              </span>
                              <span style={{ color: '#888', fontSize: 12 }}>
                                每期收款請到「結帳管理 → 分期收款」登錄
                              </span>
                            </SectionContent>
                          </Section>
                        )}

                        {(couponDeduction > 0 || appliedCoupon) && (
                          <Section>
                            <SectionTitle>
                              <Tag color='magenta' style={{ marginRight: 0 }}>
                                優惠券
                              </Tag>
                              優惠券折抵
                            </SectionTitle>
                            <SectionContent>
                              <span>
                                折抵金額：
                                <strong>{formatCurrency(couponDeduction)}</strong>
                              </span>
                              {appliedCoupon?.code && (
                                <span>
                                  優惠碼：<strong>{appliedCoupon.code}</strong>
                                </span>
                              )}
                              {appliedCoupon?.description && (
                                <span>
                                  說明：<strong>{appliedCoupon.description}</strong>
                                </span>
                              )}
                            </SectionContent>
                          </Section>
                        )}

                        <Section>
                          <SectionTitle>
                            <FilterOutlined /> 付款方式
                          </SectionTitle>
                          {renderPaymentBreakdown(payment)}
                        </Section>

                        {record.remarks && (
                          <Section>
                            <SectionTitle>
                              <EditOutlined /> 備註
                            </SectionTitle>
                            <SectionContent>
                              <div
                                style={{
                                  padding: '8px 12px',
                                  background: '#f9f9f9',
                                  borderRadius: '8px',
                                  border: '1px solid #f0f0f0',
                                  fontSize: '13px',
                                  color: '#576079',
                                  whiteSpace: 'pre-wrap'
                                }}
                              >
                                {record.remarks}
                              </div>
                            </SectionContent>
                          </Section>
                        )}

                        {/* 操作按钮 - 仅对已结帐且未作废的订单显示 */}
                        {status !== 'voided' && status !== 'draft' && (
                          <ActionButtons>
                            <Button
                              icon={<EditOutlined />}
                              onClick={() => handleUpdateDate(record)}
                            >
                              改日期
                            </Button>
                            <Button
                              icon={<EditOutlined />}
                              onClick={() => handleUpdateRemarks(record)}
                            >
                              改備註
                            </Button>
                            <Button
                              type='primary'
                              onClick={() => handleRecheckout(record)}
                            >
                              重新結帳
                            </Button>
                            <Button
                              danger
                              onClick={() => handleCancelOnly(record)}
                            >
                              取消交易
                            </Button>
                          </ActionButtons>
                        )}
                      </RecordCard>
                    )
                  })}
                </CardGrid>
              )}
            </Spin>
          </ResultWrapper>

          {records.length > 0 && (
            <PaginationWrapper>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                onChange={handlePageChange}
                onShowSizeChange={handlePageChange}
                showTotal={(total) => `共 ${total} 筆紀錄`}
              />
            </PaginationWrapper>
          )}

          {/* 改日期 Modal */}
          <Modal
            title='改日期'
            open={isUpdateDateModalVisible}
            onCancel={() => {
              setIsUpdateDateModalVisible(false)
              updateDateForm.resetFields()
              setSelectedRecordForDateUpdate(null)
            }}
            onOk={handleUpdateDateSubmit}
            okText='確認'
            cancelText='取消'
            width={isMobile ? '90%' : 500}
          >
            <Form
              form={updateDateForm}
              layout='vertical'
              initialValues={{
                date: null,
                startTime: null,
                endTime: null
              }}
            >
              <Form.Item
                name='date'
                label='日期'
                rules={[{ required: true, message: '請選擇日期' }]}
              >
                {isMobile ? (
                  <>
                    <MobileDatePicker
                      visible={updateDatePickerVisible}
                      onClose={() => setUpdateDatePickerVisible(false)}
                      precision='day'
                      value={updateDateForm.getFieldValue('date')?.toDate()}
                      onConfirm={(val) => {
                        updateDateForm.setFieldsValue({ date: dayjs(val) })
                        setUpdateDatePickerVisible(false)
                      }}
                    />
                    <div
                      onClick={() => setUpdateDatePickerVisible(true)}
                      style={{
                        cursor: 'pointer',
                        padding: '4px 11px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '2px',
                        backgroundColor: '#fff'
                      }}
                    >
                      {updateDateForm.getFieldValue('date')
                        ? dayjs(updateDateForm.getFieldValue('date')).format(
                            'YYYY-MM-DD'
                          )
                        : '選擇日期'}
                    </div>
                  </>
                ) : (
                  <DatePicker
                    style={{ width: '100%' }}
                    format='YYYY-MM-DD'
                    placeholder='選擇日期'
                  />
                )}
              </Form.Item>

              <Form.Item
                name='startTime'
                label='開始時間'
                rules={[
                  { required: true, message: '請輸入開始時間' },
                  {
                    pattern: /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/,
                    message: '時間格式必須為 HH:mm'
                  }
                ]}
              >
                <Input placeholder='HH:mm (例如: 14:30)' />
              </Form.Item>

              <Form.Item
                name='endTime'
                label='結束時間（可選）'
                rules={[
                  {
                    pattern: /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/,
                    message: '時間格式必須為 HH:mm'
                  }
                ]}
              >
                <Input placeholder='HH:mm (例如: 16:30)' />
              </Form.Item>
            </Form>
          </Modal>

          {/* 改備註 Modal */}
          <Modal
            title='改備註'
            open={isUpdateRemarksModalVisible}
            onCancel={() => {
              setIsUpdateRemarksModalVisible(false)
              updateRemarksForm.resetFields()
              setSelectedRecordForRemarksUpdate(null)
            }}
            onOk={handleUpdateRemarksSubmit}
            okText='確認'
            cancelText='取消'
            width={isMobile ? '90%' : 500}
          >
            <Form form={updateRemarksForm} layout='vertical'>
              <Form.Item name='remarks' label='備註'>
                <Input.TextArea
                  rows={4}
                  placeholder='輸入備註（可留空）'
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Form>
          </Modal>
        </PageContainer>
      </PageWrapper>
    </>
  )
}

export default CheckoutRecordsPage
